import { useEffect, useRef } from 'react';
import { findFocusable, nextFocusTarget } from '@/lib/focus-trap';

/**
 * Trap keyboard focus inside a modal container. Returns a ref to attach
 * to the dialog's outermost element. While the trap is active:
 *
 *  - Tab / Shift+Tab cycle through focusable descendants without leaving.
 *  - On mount, focus jumps to the first focusable element (or the
 *    container itself if there's nothing else).
 *  - On unmount, focus returns to the element that opened the modal.
 *
 * Pass `enabled: false` to disable temporarily without unmounting the
 * dialog (used when one modal opens another).
 */
export function useFocusTrap(enabled: boolean = true): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const root = ref.current;
    if (!root) return;

    triggerRef.current = (document.activeElement as HTMLElement | null) ?? null;

    // Defer the focus shift one tick so React has fully painted the
    // dialog children — querying focusables before mount yields none.
    queueMicrotask(() => {
      const items = findFocusable(root);
      const first = items[0] ?? root;
      first.focus();
    });

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const items = findFocusable(root);
      const target = nextFocusTarget(
        items,
        document.activeElement as HTMLElement | null,
        e.shiftKey,
      );
      if (!target) return;
      e.preventDefault();
      target.focus();
    };

    root.addEventListener('keydown', handleKeyDown);
    return (): void => {
      root.removeEventListener('keydown', handleKeyDown);
      // Restore focus to whatever opened us, if it's still in the DOM.
      const trigger = triggerRef.current;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [enabled]);

  return ref;
}
